import React, { useState, useEffect, useContext, useMemo, useCallback } from "react";
import { Link } from "react-router-dom";
import SearchIcon from "../../../static/img/SearchIcon.png";
import CalendarIcon from "../../../static/img/calendar.png";
import { MyBookingWrapper, PaginationWrapperTop } from "../MyBooking/styled/DesStyled";
import { OrderInner } from "../Order/styled/OrderStyle";
import NumberPaginationBox from "../../controls/components/numberPaginationBox/NumberPaginationBox";
import useCommonListFunctions from "../../hooks/useCommonList/useCommonListFunctions";
import { AuthenticationContext } from "../../controls/auth/AuthenticationWrap";
import { UserRole } from "../../../constants/roles";
import { formatNumber } from "../../../services/SettlementService";
import { getListDestinations } from "../../../services/LocationService";
import Location from "../../../models/Location";
import {
	getAdminPartnerPaymentRequests,
	adminRepayPartnerPaymentRequest,
	AdminPartnerPaymentRequestRow,
} from "../../../services/PartnerPaymentRequestService";
import PaymentConfirmModal from "../DebtManagement/components/PaymentConfirmModal";
import { useIsSuperAdmin } from "../../hooks/useIsSuperAdmin";
import { TranslateLabels, useLanguage } from "../../controls/layout/content/MultiLanguge";
import {
	DebtHistoryBreadcrumb,
	BreadcrumbItem,
	BreadcrumbSeparator,
	DebtHistoryContainer,
	DebtHistoryCard,
	DebtHistoryTitle,
	DebtHistorySearchForm,
	DebtHistorySearchInputWrapper,
	DebtHistorySearchInput,
	DebtHistorySearchIcon,
	DebtHistorySelect,
	DebtHistoryPeriodSelectWrapper,
	DebtHistoryPeriodSelect,
	DebtHistorySearchButton,
	DebtHistoryHeader,
	DebtHistoryPartnerName,
	DebtHistoryHeaderActions,
	DebtHistoryStatusFilter,
	DebtHistoryExportButton,
	DebtHistoryTable,
	DebtHistoryTableHeader,
	DebtHistoryTableRow,
	DebtHistoryTableCell,
	DebtHistoryStatusBadge,
	DebtHistoryStatusDot,
	DebtHistoryActionButton,
} from "./styled/DebtHistoryLookupStyles";
import PermissionGate from "../../controls/auth/PermissionGate";

const ITEMS_PER_PAGE = 15;

const formatRequestTime = (value: string | null | undefined) => {
	if (!value) return "-";
	const date = new Date(value);
	if (Number.isNaN(date.getTime())) return "-";
	return date.toLocaleString("en-GB", {
		day: "2-digit",
		month: "short",
		year: "numeric",
		hour: "2-digit",
		minute: "2-digit",
		hour12: false,
	});
};

const getDifference = (request: AdminPartnerPaymentRequestRow) => {
	const paidAmount = Number(request.paidAmount) || 0;
	const requestedAmount = Number(request.requestAmount) || 0;
	return +(paidAmount - requestedAmount).toFixed(2);
};

const getPreferredStationName = (station?: { name?: string | null; displayName?: string | null } | null) => {
	const stationName = `${station?.name || ""}`.trim();
	if (stationName) return stationName;

	const stationDisplayName = `${station?.displayName || ""}`.trim();
	if (stationDisplayName) return stationDisplayName;

	return "";
};

const getStatusLabel = (status: string | null | undefined) => {
	const normalized = String(status || "").toUpperCase();
	if (normalized === "IN_TRANSIT") return "In Transit";
	if (normalized === "PAID") return "Paid";
	return normalized || "-";
};

const getStatusStyle = (status: string | null | undefined) => {
	const normalized = String(status || "").toUpperCase();
	if (normalized === "IN_TRANSIT") {
		return { text: "In Transit", bg: "#FFFAEB", color: "#B54708" };
	}
	if (normalized === "PAID") {
		return { text: "Paid", bg: "#ECFDF3", color: "#067647" };
	}
	return { text: normalized || "-", bg: "#F3F4F6", color: "#6B7280" };
};

const getPaymentRequestTypeLabel = (requestType?: "UNDER_MIN_PAYOUT" | "FROM_MIN_PAYOUT" | null) => {
	if (requestType === "UNDER_MIN_PAYOUT") return "Below min payout";
	return "Above min payout";
};

/** Cùng mức với Pay tay (payTayGuard ở backend). */
const REPAY_REASON_MIN = 10;

export default function DebtHistoryLookup() {
	const authContext = useContext(AuthenticationContext);
	const { userInfo, isFinishedValidate } = authContext || {};
	const { lang } = useLanguage();

	if (isFinishedValidate && (!userInfo || userInfo.role !== UserRole.Admin)) {
		return (
			<MyBookingWrapper>
				<OrderInner>
					<div style={{ textAlign: "center", padding: "50px" }}>
						<h2>Access Denied</h2>
						<p>You don't have permission to access this page.</p>
					</div>
				</OrderInner>
			</MyBookingWrapper>
		);
	}

	const [searchInput, setSearchInput] = useState("");
	const [searchTerm, setSearchTerm] = useState("");
	const [selectedRegion, setSelectedRegion] = useState("all");
	const [selectedPeriod, setSelectedPeriod] = useState("all");
	const [selectedStatus, setSelectedStatus] = useState("all");
	const [sortField, setSortField] = useState<string>("period");
	const [sortOrder, setSortOrder] = useState<"ASC" | "DESC">("DESC");
	const [requests, setRequests] = useState<AdminPartnerPaymentRequestRow[]>([]);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [destinations, setDestinations] = useState<Location[]>([]);
	const [refreshing, setRefreshing] = useState(false);
	const [isRepayModalOpen, setIsRepayModalOpen] = useState(false);
	const [pendingRepayRequest, setPendingRepayRequest] = useState<AdminPartnerPaymentRequestRow | null>(null);
	const [repayAmountInput, setRepayAmountInput] = useState("");
	const [repayReason, setRepayReason] = useState("");
	const { isSuperAdmin } = useIsSuperAdmin();
	const [isRepaying, setIsRepaying] = useState(false);
	const { page, doChangePage } = useCommonListFunctions();

	const parsePeriod = (period: string) => {
		if (period === "all") return { month: undefined, year: undefined };
		const parts = period.split("/");
		if (parts.length === 2) {
			return { month: parseInt(parts[0], 10), year: parseInt(parts[1], 10) };
		}
		return { month: undefined, year: undefined };
	};

	const loadRequests = useCallback(async () => {
		try {
			setLoading(true);
			setError(null);
			const rows = await getAdminPartnerPaymentRequests();
			setRequests(rows || []);
		} catch (err: any) {
			console.error("Error loading payment request history:", err);
			setError(err?.message || "Có lỗi xảy ra khi tải dữ liệu");
			setRequests([]);
		} finally {
			setLoading(false);
		}
	}, []);

	useEffect(() => {
		const loadDestinations = async () => {
			try {
				const { data } = await getListDestinations(1, 100);
				setDestinations(data);
			} catch (err) {
				console.error("Error loading destinations:", err);
			}
		};
		loadDestinations();
	}, []);

	useEffect(() => {
		loadRequests();
	}, [loadRequests]);

	const filteredRequests = useMemo(() => {
		const keyword = searchTerm.trim().toLowerCase();
		const { month, year } = parsePeriod(selectedPeriod);

		const rows = requests.filter((row) => {
			const stationName = String(row.station?.name || "").toLowerCase();
			const stationDisplayName = String(row.station?.displayName || "").toLowerCase();
			const partnerIdText = String(row.partnerId || "");
			const status = String(row.status || "").toUpperCase();
			const diff = getDifference(row);
			const requestDate = new Date(row.requestTime);

			if (
				keyword &&
				!stationName.includes(keyword) &&
				!stationDisplayName.includes(keyword) &&
				!partnerIdText.includes(keyword)
			) {
				return false;
			}

			if (selectedRegion !== "all") {
				const destinationId = row.station?.destinationId;
				if (!destinationId || String(destinationId) !== selectedRegion) {
					return false;
				}
			}

			if (month && year) {
				if (requestDate.getMonth() + 1 !== month || requestDate.getFullYear() !== year) {
					return false;
				}
			}

			if (selectedStatus === "IN_TRANSIT" && status !== "IN_TRANSIT") return false;
			if (selectedStatus === "PAID" && status !== "PAID") return false;
			if (selectedStatus === "NEED_REPAY" && !(status === "PAID" && diff < 0)) return false;

			return true;
		});

		rows.sort((a, b) => {
			const factor = sortOrder === "ASC" ? 1 : -1;
			if (sortField === "totalIncome") {
				return (Number(a.requestAmount) - Number(b.requestAmount)) * factor;
			}
			if (sortField === "bookingCount") {
				return ((Number(a.orderCount) || 0) - (Number(b.orderCount) || 0)) * factor;
			}
			if (sortField === "status") {
				return getStatusLabel(a.status).localeCompare(getStatusLabel(b.status)) * factor;
			}
			const timeA = new Date(a.requestTime).getTime();
			const timeB = new Date(b.requestTime).getTime();
			return (timeA - timeB) * factor;
		});

		return rows;
	}, [requests, searchTerm, selectedRegion, selectedPeriod, selectedStatus, sortField, sortOrder]);

	const pagedRequests = useMemo(() => {
		const currentPage = page || 1;
		const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
		return filteredRequests.slice(startIndex, startIndex + ITEMS_PER_PAGE);
	}, [filteredRequests, page]);

	useEffect(() => {
		const currentPage = page || 1;
		const maxPage = Math.max(1, Math.ceil(filteredRequests.length / ITEMS_PER_PAGE));
		if (currentPage > maxPage) {
			doChangePage?.(1);
		}
	}, [filteredRequests.length, page, doChangePage]);

	const handleSearch = () => {
		setSearchTerm(searchInput);
		doChangePage?.(1);
	};

	const handleSort = (field: string) => {
		if (sortField === field) {
			setSortOrder(sortOrder === "ASC" ? "DESC" : "ASC");
		} else {
			setSortField(field);
			setSortOrder("DESC");
		}
	};

	const handleRefresh = async () => {
		try {
			setRefreshing(true);
			await loadRequests();
		} finally {
			setRefreshing(false);
		}
	};

	const openRepayModal = (request: AdminPartnerPaymentRequestRow) => {
		const diff = getDifference(request);
		const suggested = Math.abs(diff);
		setPendingRepayRequest(request);
		setRepayAmountInput(suggested > 0 ? suggested.toFixed(2) : "");
		setRepayReason("");
		setIsRepayModalOpen(true);
	};

	const closeRepayModal = () => {
		if (isRepaying) return;
		setIsRepayModalOpen(false);
		setPendingRepayRequest(null);
		setRepayAmountInput("");
		setRepayReason("");
	};

	const handleRepayConfirm = async () => {
		if (!pendingRepayRequest) return;
		const repayAmount = Number((repayAmountInput || "").replace(/,/g, "."));
		if (!Number.isFinite(repayAmount) || repayAmount <= 0) {
			alert("Vui lòng nhập số tiền hợp lệ lớn hơn 0.");
			return;
		}
		if (repayReason.trim().length < REPAY_REASON_MIN) {
			alert(`Enter a reason (at least ${REPAY_REASON_MIN} characters) for this repay.`);
			return;
		}

		try {
			setIsRepaying(true);
			await adminRepayPartnerPaymentRequest(pendingRepayRequest.id, repayAmount, repayReason.trim());
			await loadRequests();
			closeRepayModal();
			alert("Đã repay thành công.");
		} catch (err: any) {
			console.error("Error repay payment request:", err);
			alert(`Có lỗi xảy ra khi repay: ${err?.response?.data?.message || err.message || "Vui lòng thử lại"}`);
		} finally {
			setIsRepaying(false);
		}
	};

	const getPartnerTitle = () => {
		if (searchTerm.trim()) return searchTerm.trim();
		return "Tất cả station";
	};

	const generatePeriodOptions = () => {
		const options = [{ value: "all", label: "Chọn kỳ công nợ" }];
		const now = new Date();
		for (let i = 0; i < 12; i++) {
			const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
			const month = String(date.getMonth() + 1).padStart(2, "0");
			const year = date.getFullYear();
			options.push({ value: `${month}/${year}`, label: `${month}/${year}` });
		}
		return options;
	};

	const periodOptions = generatePeriodOptions();

	return (
		<MyBookingWrapper>
			<OrderInner>
				<DebtHistoryBreadcrumb>
					<BreadcrumbItem
						as={Link}
						to="/">
						{TranslateLabels[lang]?.DEBT_BREADCRUMB_HOME || "Home"}
					</BreadcrumbItem>
					<BreadcrumbSeparator
						src="./static/img/direction-right.png"
						alt=">"
					/>
					<BreadcrumbItem
						as={Link}
						to="/debt-management">
						{TranslateLabels[lang]?.DEBT_BREADCRUMB_MANAGEMENT || "Quản lý công nợ"}
					</BreadcrumbItem>
					<BreadcrumbSeparator
						src="./static/img/direction-right.png"
						alt=">"
					/>
					<BreadcrumbItem active>Lịch sử công nợ</BreadcrumbItem>
				</DebtHistoryBreadcrumb>
			</OrderInner>

			<DebtHistoryContainer>
				<DebtHistoryCard>
					<DebtHistoryTitle>Tra cứu dữ liệu lịch sử công nợ</DebtHistoryTitle>
					<DebtHistorySearchForm>
						<DebtHistorySearchInputWrapper>
							<DebtHistorySearchIcon
								src={SearchIcon}
								alt="Search"
							/>
							<DebtHistorySearchInput
								type="text"
								placeholder="Partner_HN_StationA"
								value={searchInput}
								onChange={(e) => setSearchInput(e.target.value)}
								onKeyDown={(e) => {
									if (e.key === "Enter") {
										handleSearch();
									}
								}}
							/>
						</DebtHistorySearchInputWrapper>

						<DebtHistorySelect
							value={selectedRegion}
							onChange={(e) => {
								setSelectedRegion(e.target.value);
								doChangePage?.(1);
							}}>
							<option value="all">Chọn khu vực</option>
							{destinations
								.filter((d) => d.id && d.lat && d.long)
								.map((destination) => (
									<option
										key={destination.id}
										value={destination.id || ""}>
										{destination.name || ""}
									</option>
								))}
						</DebtHistorySelect>

						<DebtHistoryPeriodSelectWrapper>
							<DebtHistoryPeriodSelect
								value={selectedPeriod}
								onChange={(e) => {
									setSelectedPeriod(e.target.value);
									doChangePage?.(1);
								}}
								calendarIcon={CalendarIcon}>
								{periodOptions.map((option) => (
									<option
										key={option.value}
										value={option.value}>
										{option.label}
									</option>
								))}
							</DebtHistoryPeriodSelect>
						</DebtHistoryPeriodSelectWrapper>

						<DebtHistorySearchButton onClick={handleSearch}>Tìm kiếm</DebtHistorySearchButton>
					</DebtHistorySearchForm>
				</DebtHistoryCard>

				<DebtHistoryHeader>
					<DebtHistoryPartnerName>{getPartnerTitle()}</DebtHistoryPartnerName>
					<DebtHistoryHeaderActions>
						<DebtHistoryStatusFilter
							value={selectedStatus}
							onChange={(e) => {
								setSelectedStatus(e.target.value);
								doChangePage?.(1);
							}}>
							<option value="all">Tất cả trạng thái</option>
							<option value="IN_TRANSIT">In Transit</option>
							<option value="PAID">Paid</option>
							<option value="NEED_REPAY">Cần repay</option>
						</DebtHistoryStatusFilter>
						<DebtHistoryExportButton
							onClick={handleRefresh}
							disabled={refreshing}>
							<img
								src="./static/img/debt/download.png"
								alt="Refresh"
								style={{ width: "16px", height: "16px" }}
							/>
							{refreshing ? "Đang tải..." : "Tải lại"}
						</DebtHistoryExportButton>
					</DebtHistoryHeaderActions>
				</DebtHistoryHeader>

				<DebtHistoryTable>
					<DebtHistoryTableHeader>
						<DebtHistoryTableCell
							style={{ cursor: "pointer", userSelect: "none" }}
							onClick={() => handleSort("period")}>
							Request
						</DebtHistoryTableCell>
						<DebtHistoryTableCell
							style={{ cursor: "pointer", userSelect: "none" }}
							onClick={() => handleSort("status")}>
							Trạng thái
						</DebtHistoryTableCell>
						<DebtHistoryTableCell
							style={{ cursor: "pointer", userSelect: "none" }}
							onClick={() => handleSort("bookingCount")}>
							Số booking
						</DebtHistoryTableCell>
						<DebtHistoryTableCell
							style={{ cursor: "pointer", userSelect: "none" }}
							onClick={() => handleSort("totalIncome")}>
							Số tiền yêu cầu
						</DebtHistoryTableCell>
						<DebtHistoryTableCell>Số tiền đã trả</DebtHistoryTableCell>
						<DebtHistoryTableCell>Chênh lệch</DebtHistoryTableCell>
						<DebtHistoryTableCell>Thời gian yêu cầu</DebtHistoryTableCell>
						<DebtHistoryTableCell>Type</DebtHistoryTableCell>
						<DebtHistoryTableCell>Thời gian thanh toán</DebtHistoryTableCell>
						<DebtHistoryTableCell>Hành động</DebtHistoryTableCell>
					</DebtHistoryTableHeader>

					{loading ? (
						<DebtHistoryTableRow backgroundColor="#FFFFFF">
							<div style={{ textAlign: "center", padding: "20px", gridColumn: "1 / -1" }}>
								Đang tải dữ liệu...
							</div>
						</DebtHistoryTableRow>
					) : error ? (
						<DebtHistoryTableRow backgroundColor="#FFFFFF">
							<div
								style={{
									textAlign: "center",
									padding: "20px",
									color: "#EF4444",
									gridColumn: "1 / -1",
								}}>
								{error}
							</div>
						</DebtHistoryTableRow>
					) : pagedRequests.length === 0 ? (
						<DebtHistoryTableRow backgroundColor="#FFFFFF">
							<div style={{ textAlign: "center", padding: "20px", gridColumn: "1 / -1" }}>
								Không có dữ liệu
							</div>
						</DebtHistoryTableRow>
					) : (
						pagedRequests.map((request) => {
							const statusStyle = getStatusStyle(request.status);
							const difference = getDifference(request);
							const isNegativeDifference =
								String(request.status).toUpperCase() === "PAID" && difference < 0;

							return (
								<DebtHistoryTableRow key={request.id}>
									<DebtHistoryTableCell>
										<div>
											<div style={{ fontWeight: 700 }}>Request #{request.id}</div>
											{/* Tên điểm — trước chỉ có mã số (Quân 11/09: "để mỗi Station ID thì làm sao
											    biết Station nào"). API đã trả sẵn station.name, chỉ chưa hiển thị. */}
											{getPreferredStationName(request.station) ? (
												<div style={{ fontSize: "13px", color: "#111827" }}>
													{getPreferredStationName(request.station)}
												</div>
											) : null}
											<div style={{ fontSize: "12px", color: "#6B7280" }}>
												Partner #{request.partnerId}
											</div>
										</div>
									</DebtHistoryTableCell>
									<DebtHistoryTableCell>
										<DebtHistoryStatusBadge
											backgroundColor={statusStyle.bg}
											textColor={statusStyle.color}>
											<DebtHistoryStatusDot color={statusStyle.color} />
											{statusStyle.text}
										</DebtHistoryStatusBadge>
									</DebtHistoryTableCell>
									<DebtHistoryTableCell>{Number(request.orderCount) || 0}</DebtHistoryTableCell>
									<DebtHistoryTableCell>
										{formatNumber(Number(request.requestAmount) || 0)}
									</DebtHistoryTableCell>
									<DebtHistoryTableCell>
										{formatNumber(Number(request.paidAmount) || 0)}
									</DebtHistoryTableCell>
									<DebtHistoryTableCell
										style={{
											fontWeight: 700,
											color: difference < 0 ? "#B42318" : "#067647",
										}}>
										{formatNumber(difference)}
									</DebtHistoryTableCell>
									<DebtHistoryTableCell>
										{formatRequestTime(request.requestTime)}
									</DebtHistoryTableCell>
									<DebtHistoryTableCell>
										{getPaymentRequestTypeLabel(request.requestType)}
									</DebtHistoryTableCell>
									<DebtHistoryTableCell>
										{request.paidTime ? formatRequestTime(request.paidTime) : "-"}
									</DebtHistoryTableCell>
									<DebtHistoryTableCell>
										{isNegativeDifference ? (
											// 04/11/2026 (Quân): Repay cùng rào với Pay tay — chỉ super admin.
											isSuperAdmin ? (
												<PermissionGate permission="settlements.manage">
													<DebtHistoryActionButton onClick={() => openRepayModal(request)}>
														Repay
													</DebtHistoryActionButton>
												</PermissionGate>
											) : (
												<span style={{ fontSize: "12px", color: "#64748B" }}>Super admin only</span>
											)
										) : (
											<span style={{ fontSize: "12px", color: "#64748B" }}>
												{String(request.status).toUpperCase() === "IN_TRANSIT"
													? "Đang chờ xử lý"
													: "Đã đủ"}
											</span>
										)}
									</DebtHistoryTableCell>
								</DebtHistoryTableRow>
							);
						})
					)}
				</DebtHistoryTable>

				{filteredRequests.length > 0 && (
					<PaginationWrapperTop>
						<NumberPaginationBox
							page={page || 1}
							count={filteredRequests.length}
							per={ITEMS_PER_PAGE}
							onChange={(val) => doChangePage?.(val)}
						/>
					</PaginationWrapperTop>
				)}
			</DebtHistoryContainer>

			<PaymentConfirmModal
				isOpen={isRepayModalOpen}
				onClose={closeRepayModal}
				onConfirm={handleRepayConfirm}
				stationName={
					getPreferredStationName(pendingRepayRequest?.station) ||
					(pendingRepayRequest ? `Station #${pendingRepayRequest.partnerId}` : "")
				}
				amountHintText={formatNumber(
					Math.abs(getDifference(pendingRepayRequest || ({} as AdminPartnerPaymentRequestRow)))
				)}
				amountValue={repayAmountInput}
				onAmountChange={setRepayAmountInput}
				noteValue={repayReason}
				onNoteChange={setRepayReason}
				noteRequiredMin={REPAY_REASON_MIN}
				isSubmitting={isRepaying}
			/>
		</MyBookingWrapper>
	);
}
